import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Focus, MousePointer2, RotateCcw, X } from 'lucide-react';
import {
  type ScreenPos,
  type WorldPos,
  projectToScreen,
} from '../utils/measureUtils';

interface FocusToolProps {
  viewerRef: React.MutableRefObject<any>;
  isActive: boolean;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLButtonElement | null>;
  initialTarget?: {
    label?: string;
    center: [number, number, number];
    size?: [number, number, number];
  };
}

type FocusStep = 'capturing' | 'focused';

interface FocusTarget {
  label: string;
  center: WorldPos;
}

interface FrozenViewerState {
  movementSpeed: number;
  lookSpeed: number;
  maxClickToMoveDistance: number;
}

const FOCUS_WORLD_RADIUS = 4.2;
const MIN_APERTURE_PX = 150;
const MAX_APERTURE_PX = 520;
const ORBIT_DURATION_MS = 12000;
const MIN_ORBIT_RADIUS = 3;

function worldPosFromTuple(tuple: [number, number, number]): WorldPos {
  return { x: tuple[0], y: tuple[1], z: tuple[2] };
}

function pickWorldPointFromPointer(
  viewer: any,
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number
): WorldPos | null {
  const camera = viewer?.camera;
  const raycaster = viewer?.raycaster;
  const splatMesh = viewer?.splatMesh;
  const getRenderDimensions = viewer?.getRenderDimensions;

  if (!camera || !raycaster || !splatMesh || !getRenderDimensions) return null;

  const rect = canvas.getBoundingClientRect();
  const renderDimensions = new THREE.Vector2();
  const outHits: unknown[] = [];

  getRenderDimensions.call(viewer, renderDimensions);
  camera.updateMatrixWorld?.();

  const screenPosition = new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * renderDimensions.x,
    ((clientY - rect.top) / rect.height) * renderDimensions.y
  );

  raycaster.setFromCameraAndScreenPosition(camera, screenPosition, renderDimensions);
  const returnValue = raycaster.intersectSplatMesh(splatMesh, outHits);
  const hits = Array.isArray(returnValue) ? returnValue : outHits;
  const origin = (hits[0] as any)?.origin;

  const x = Number(origin?.x);
  const y = Number(origin?.y);
  const z = Number(origin?.z);

  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return null;
  return { x, y, z };
}

function setOverlayBackground(
  el: HTMLDivElement,
  screenPoint: ScreenPos | null,
  targetVisible: boolean,
  aperturePx: number
) {
  if (!screenPoint || !targetVisible) {
    el.style.background = 'rgba(0,0,0,0.9)';
    return;
  }

  const featherPx = Math.max(86, aperturePx * 0.45);
  el.style.background = `radial-gradient(circle ${aperturePx + featherPx}px at ${screenPoint.x}px ${screenPoint.y}px, transparent 0 ${aperturePx}px, rgba(0,0,0,0.55) ${aperturePx + 24}px, rgba(0,0,0,0.94) ${aperturePx + featherPx}px)`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function computeProjectedAperturePx(
  center: WorldPos,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement
): number {
  const centerScreen = projectToScreen(center, camera, canvas);
  const radiusScreen = projectToScreen(
    { x: center.x + FOCUS_WORLD_RADIUS, y: center.y, z: center.z },
    camera,
    canvas
  );

  if (!centerScreen.visible || !radiusScreen.visible) return MIN_APERTURE_PX;

  const dx = radiusScreen.x - centerScreen.x;
  const dy = radiusScreen.y - centerScreen.y;
  return clamp(Math.sqrt(dx * dx + dy * dy), MIN_APERTURE_PX, MAX_APERTURE_PX);
}

const FocusTool: React.FC<FocusToolProps> = ({
  viewerRef,
  isActive,
  onClose,
  anchorRef,
  initialTarget,
}) => {
  const [step, setStep] = useState<FocusStep>('capturing');
  const [target, setTarget] = useState<FocusTarget | null>(null);
  const [panelRight, setPanelRight] = useState<number | null>(null);

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number>(0);
  const orbitRafRef = useRef<number>(0);
  const isOrbitingRef = useRef(false);
  const targetRef = useRef<FocusTarget | null>(null);
  const frozenViewerStateRef = useRef<FrozenViewerState | null>(null);

  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  useEffect(() => {
    if (!anchorRef?.current) return;
    const update = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setPanelRight(window.innerWidth - rect.right);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [anchorRef, isActive]);

  const restoreViewerControls = useCallback(() => {
    const viewer = viewerRef.current;
    const frozen = frozenViewerStateRef.current;
    if (!viewer || !frozen) return;

    if (viewer.controls) {
      viewer.controls.movementSpeed = frozen.movementSpeed;
      viewer.controls.lookSpeed = frozen.lookSpeed;
    }
    viewer.maxClickToMoveDistance = frozen.maxClickToMoveDistance;
    viewer.clickTransitioning = false;
    viewer.transitioningCameraTarget = false;
    if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
    if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);

    frozenViewerStateRef.current = null;
  }, [viewerRef]);

  const stopOrbit = useCallback(() => {
    if (orbitRafRef.current) {
      cancelAnimationFrame(orbitRafRef.current);
      orbitRafRef.current = 0;
    }
    isOrbitingRef.current = false;
  }, []);

  const constrainViewerControls = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer || frozenViewerStateRef.current) return;

    frozenViewerStateRef.current = {
      movementSpeed: viewer.controls?.movementSpeed ?? 5,
      lookSpeed: viewer.controls?.lookSpeed ?? 10,
      maxClickToMoveDistance: viewer.maxClickToMoveDistance ?? 15,
    };

    if (viewer.controls) {
      viewer.controls.movementSpeed = viewer.controls.movementSpeed ?? 5;
      viewer.controls.lookSpeed = viewer.controls.lookSpeed ?? 10;
    }
    viewer.maxClickToMoveDistance = 3;
    viewer.clickTransitioning = false;
    viewer.transitioningCameraTarget = false;
    if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
    if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);
  }, [viewerRef]);

  const startOrbitAroundFocus = useCallback(() => {
    const viewer = viewerRef.current;
    const camera = viewer?.camera;
    const currentTarget = targetRef.current;
    if (!viewer || !camera?.position || !currentTarget) return;

    if (isOrbitingRef.current) {
      stopOrbit();
      return;
    }

    stopOrbit();

    const centerVector = new THREE.Vector3(
      currentTarget.center.x,
      currentTarget.center.y,
      currentTarget.center.z
    );
    const startPosition = camera.position.clone();
    const radialOffset = new THREE.Vector3(
      startPosition.x - centerVector.x,
      0,
      startPosition.z - centerVector.z
    );

    let radius = radialOffset.length();
    if (radius < MIN_ORBIT_RADIUS) {
      radius = MIN_ORBIT_RADIUS;
      radialOffset.set(radius, 0, 0);
    }

    const heightOffset = startPosition.y - centerVector.y;
    const startAngle = Math.atan2(radialOffset.z, radialOffset.x);
    const orbitStartMs = performance.now();
    isOrbitingRef.current = true;

    viewer.clickTransitioning = false;
    viewer.transitioningCameraTarget = false;
    if (viewer.nextCamera) viewer.nextCamera.copy(camera);
    if (viewer.previousCamera) viewer.previousCamera.copy(camera);

    const animate = (now: number) => {
      const activeViewer = viewerRef.current;
      const activeCamera = activeViewer?.camera;
      if (!activeViewer || !activeCamera || !targetRef.current) {
        stopOrbit();
        return;
      }

      const progress = Math.min((now - orbitStartMs) / ORBIT_DURATION_MS, 1);
      const easedProgress = 0.5 - 0.5 * Math.cos(progress * Math.PI);
      const angle = startAngle + easedProgress * Math.PI * 2;

      activeCamera.position.set(
        centerVector.x + Math.cos(angle) * radius,
        centerVector.y + heightOffset,
        centerVector.z + Math.sin(angle) * radius
      );
      activeCamera.lookAt(centerVector);
      activeCamera.updateMatrixWorld(true);

      activeViewer.forceRenderNextFrame?.();

      if (progress < 1) {
        orbitRafRef.current = requestAnimationFrame(animate);
        return;
      }

      stopOrbit();
      if (activeViewer.controls?.updateLookDirection) {
        activeViewer.controls.updateLookDirection();
      }
    };

    orbitRafRef.current = requestAnimationFrame(animate);
  }, [stopOrbit, viewerRef]);

  const resetCapture = useCallback(() => {
    stopOrbit();
    restoreViewerControls();
    setTarget(null);
    setStep('capturing');
  }, [restoreViewerControls, stopOrbit]);

  useEffect(() => {
    if (!isActive) {
      resetCapture();
      return;
    }

    if (initialTarget) {
      setTarget({
        label: initialTarget.label || 'Focus target',
        center: worldPosFromTuple(initialTarget.center),
      });
      setStep('focused');
    } else {
      setStep('capturing');
    }
  }, [initialTarget, isActive, resetCapture]);

  useEffect(() => {
    if (!isActive || step !== 'focused') {
      stopOrbit();
      restoreViewerControls();
      return;
    }
    constrainViewerControls();
    return () => {
      stopOrbit();
      restoreViewerControls();
    };
  }, [constrainViewerControls, isActive, restoreViewerControls, step, stopOrbit]);

  useEffect(() => {
    if (!isActive || step !== 'focused') return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return;
      event.preventDefault();
      event.stopPropagation();
      startOrbitAroundFocus();
    };

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isActive, startOrbitAroundFocus, step]);

  useEffect(() => {
    if (!isActive || step !== 'capturing') return;

    const viewer = viewerRef.current;
    const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
    if (!viewer || !canvas) return;

    const blockViewerNavigation = () => {
      viewer.clickTransitioning = false;
      viewer.transitioningCameraTarget = false;
      if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
      if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);
    };

    const onPointerDown = (event: PointerEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      blockViewerNavigation();
    };

    const onPointerUp = (event: PointerEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      blockViewerNavigation();

      const point = pickWorldPointFromPointer(viewer, canvas, event.clientX, event.clientY);
      if (!point) return;

      const cam = viewerRef.current?.camera;
      if (cam?.position) {
        console.log('[FocusTool] camera position', {
          x: Number(cam.position.x.toFixed(2)),
          y: Number(cam.position.y.toFixed(2)),
          z: Number(cam.position.z.toFixed(2)),
        });
      }
      console.log('[FocusTool] focus target', {
        x: Number(point.x.toFixed(2)),
        y: Number(point.y.toFixed(2)),
        z: Number(point.z.toFixed(2)),
      });

      setTarget({
        label: 'Focused object',
        center: point,
      });
      setStep('focused');
    };

    canvas.addEventListener('pointerdown', onPointerDown, true);
    canvas.addEventListener('pointerup', onPointerUp, true);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown, true);
      canvas.removeEventListener('pointerup', onPointerUp, true);
    };
  }, [isActive, step, viewerRef]);

  useEffect(() => {
    if (!isActive || step !== 'focused') return;

    const tick = () => {
      const viewer = viewerRef.current;
      const overlay = overlayRef.current;
      const currentTarget = targetRef.current;
      const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
      const camera = viewer?.camera;

      if (overlay && canvas && camera && currentTarget) {
        const projected = projectToScreen(currentTarget.center, camera, canvas);
        const aperturePx = computeProjectedAperturePx(currentTarget.center, camera, canvas);
        setOverlayBackground(overlay, projected, projected.visible, aperturePx);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isActive, step, viewerRef]);

  useEffect(() => {
    return () => {
      stopOrbit();
      cancelAnimationFrame(rafRef.current);
      restoreViewerControls();
    };
  }, [restoreViewerControls, stopOrbit]);

  if (!isActive) return null;

  const panelStyleRight = panelRight !== null ? panelRight : 16;

  return (
    <>
      {step === 'capturing' && <style>{`canvas { cursor: crosshair !important; }`}</style>}

      {step === 'focused' && (
          <div
            ref={overlayRef}
            className="fixed inset-0 z-[1000] pointer-events-none"
            style={{ background: 'rgba(0,0,0,0.9)' }}
          />
      )}

      <div
        className="fixed z-[1002] w-72 overflow-hidden rounded-2xl border border-white/12 bg-black/65 text-white shadow-2xl backdrop-blur-xl"
        style={{ top: 68, right: panelStyleRight }}
      >
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <Focus className="h-4 w-4 text-[#0CB4E4]" />
            <span className="text-sm font-semibold">Focus</span>
          </div>
          <button
            type="button"
            onClick={() => {
              resetCapture();
              onClose();
            }}
            className="grid h-7 w-7 place-items-center rounded-lg text-white/55 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close focus tool"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-4 py-4">
          {step === 'capturing' ? (
            <div className="rounded-xl border border-[#0CB4E4]/20 bg-[#0CB4E4]/10 px-3 py-3">
              <div className="mb-2 flex items-center gap-2 text-sm font-medium text-white">
                <MousePointer2 className="h-4 w-4 text-[#0CB4E4]" />
                Click the object center
              </div>
              <p className="text-xs leading-5 text-white/62">
                Pick the middle of the object you want to inspect. Focus mode will dim the periphery and frame the target.
              </p>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3">
                <div className="text-xs uppercase tracking-wide text-white/38">Target</div>
                <div className="mt-1 text-sm font-semibold text-white">{target?.label ?? 'Focused object'}</div>
                {target && (
                  <div className="mt-2 font-mono text-[10px] leading-5 text-white/50">
                    X {target.center.x.toFixed(2)}<br />
                    Y {target.center.y.toFixed(2)}<br />
                    Z {target.center.z.toFixed(2)}
                  </div>
                )}
              </div>

              <p className="text-xs leading-5 text-white/52">
                Focus mode dims the scene while keeping keyboard navigation available. Press Spacebar to start or stop a slow fly-around of the highlighted object.
              </p>

              <button
                type="button"
                onClick={resetCapture}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.06] px-3 py-2 text-xs font-semibold text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Pick another object
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default FocusTool;
